import { z } from "zod";
import { apiClient } from "../../services/api";

/**
 * What an address turns out to be, before anything is sent to it.
 *
 * One place rather than four, because the four surfaces that ask (the signup
 * page, the welcome sheet, the desktop gate, the login page) must all send the
 * code with the SAME intent: the server refuses a login code for an unknown
 * address and a signup code for a known one, and `verify` burns the code either
 * way. A door that skips this check opens the six boxes over a mailbox no mail
 * is going to reach.
 */
export type EmailAuthOutcome =
  /** Not an address. Nothing was sent. */
  | { kind: "invalid" }
  /** The check itself failed (network, server). Nothing is known. */
  | { kind: "failed" }
  /** The address has an account. A code sent here is a LOGIN. */
  | { kind: "existing" }
  /** The address has none. A code sent here is a SIGNUP. */
  | { kind: "new" };

/**
 * Classify an address. Deliberately does not navigate and does not toast: the
 * four surfaces that ask this question go to different places with the answer
 * and word it differently, and a helper that decided for them would be a fifth
 * opinion rather than one fewer.
 */
export async function classifyEmailForAuth(
  email: string
): Promise<EmailAuthOutcome> {
  const trimmed = email.trim();
  if (!z.email().safeParse(trimmed).success) return { kind: "invalid" };

  try {
    const res = await apiClient.get("/webapp/public/auth/check-email", {
      query: { email: trimmed },
    });
    if (res.body.isExisting) return { kind: "existing" };
    return { kind: "new" };
  } catch {
    return { kind: "failed" };
  }
}
